import { bus } from '@/core/EventBus';

/**
 * De langlevende staat van het spel: geld, reputatie, territorium en luitenants.
 *
 * Dit object bestaat al voordat de tafellaag er is. Dat is bewust: de wisselwerking straat ↔
 * tafel is volgens `docs/DESIGN.md` het centrale mechanisme, en die werkt alleen als
 * straatacties vanaf het begin naar dezelfde plek schrijven. Achteraf een centrale staat
 * inbouwen betekent de straatlaag herschrijven.
 *
 * Systemen lezen hier direct uit, maar schrijven bij voorkeur via de methodes: die
 * begrenzen waardes en zenden events uit. Niemand luistert naar `GameState` zelf — de
 * koppeling loopt via `EventBus`, zodat de tafellaag later ingeplugd kan worden zonder dat
 * de straatlaag hem hoeft te kennen.
 */

export type ReputationTrack = 'police' | 'populace' | 'rivals';
export type LieutenantId = 'bruno' | 'rocco' | 'dante';
export type DistrictId =
  | 'harbor'
  | 'workers'
  | 'bellano'
  | 'diamond'
  | 'tropics'
  | 'steel'
  | 'centre'
  | 'suburbs';
export type Faction = 'vitale' | 'barili' | 'neutral';
export type RelationshipId = 'johnello' | 'naftay' | 'tyrone' | LieutenantId;

/** Reputatie loopt van 0 tot 100; 50 is "niemand let bijzonder op je". */
export const REPUTATION_MIN = 0;
export const REPUTATION_MAX = 100;
export const REPUTATION_START = 50;

/** Waar een opgeslagen spel aan moet voldoen om geladen te worden. */
const SAVE_KEY = 'newamsterdam.save';
const SAVE_VERSION = 1;

export interface Lieutenant {
  readonly id: LieutenantId;
  readonly name: string;
  /** 0..1 — hoeveel hij zelf de baas wil zijn. Hoog is bruikbaar én gevaarlijk. */
  ambition: number;
  /** 0..1 — hoe ver hij meegaat als het tegenzit. */
  loyalty: number;
}

export interface Territory {
  readonly district: DistrictId;
  owner: Faction;
  /** Opbrengst per economie-tick. Nul zolang de wijk niet van jou is. */
  income: number;
  /** 0..1 — onder de 0.35 gaat een wijk inkomen verliezen of wordt hij overvallen. */
  stability: number;
  /** Wie de wijk beheert. Zonder beheerder zakt de stabiliteit. */
  lieutenant: LieutenantId | null;
}

/** De vorm waarin een spel op schijf staat. Bewust plat en zonder klassen. */
export interface SaveData {
  version: number;
  money: number;
  reputation: Record<ReputationTrack, number>;
  flags: Record<string, boolean>;
  relationships: Record<string, number>;
  lieutenants: Lieutenant[];
  territory: Territory[];
}

/** Minimale opslag-interface, zodat tests niet van `localStorage` afhangen. */
export interface StateStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const LIEUTENANT_NAMES: Record<LieutenantId, string> = {
  bruno: 'Bruno Cattaneo',
  rocco: 'Rocco Marino',
  dante: 'Dante Ricci',
};

/**
 * Beginstand: Luca heeft de haven, een handvol mensen en verder niets. De rest van de stad
 * is van Barili of van niemand. Zie `docs/STORY_BIBLE.md` — hij begint met bijna niets.
 */
function initialTerritory(): Territory[] {
  const owned: DistrictId[] = ['harbor'];
  const rival: DistrictId[] = ['centre', 'suburbs'];
  const all: DistrictId[] = [
    'harbor',
    'workers',
    'bellano',
    'diamond',
    'tropics',
    'steel',
    'centre',
    'suburbs',
  ];

  return all.map((district) => ({
    district,
    owner: owned.includes(district) ? 'vitale' : rival.includes(district) ? 'barili' : 'neutral',
    income: owned.includes(district) ? 250 : 0,
    stability: owned.includes(district) ? 0.6 : 0.5,
    lieutenant: null,
  }));
}

function initialLieutenants(): Lieutenant[] {
  return [
    { id: 'bruno', name: LIEUTENANT_NAMES.bruno, ambition: 0.7, loyalty: 0.55 },
    { id: 'rocco', name: LIEUTENANT_NAMES.rocco, ambition: 0.3, loyalty: 0.85 },
    { id: 'dante', name: LIEUTENANT_NAMES.dante, ambition: 0.55, loyalty: 0.7 },
  ];
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Valt terug op geheugen als er geen `localStorage` is — in tests, of bij geweigerde opslag. */
export function memoryStorage(): StateStorage {
  const map = new Map<string, string>();
  return {
    getItem: (key) => map.get(key) ?? null,
    setItem: (key, value) => void map.set(key, value),
    removeItem: (key) => void map.delete(key),
  };
}

function defaultStorage(): StateStorage {
  try {
    const candidate = globalThis.localStorage as StateStorage | undefined;
    if (candidate) {
      // Schrijfrecht meteen toetsen: in privémodus bestaat localStorage wel maar gooit hij.
      const probe = `${SAVE_KEY}.probe`;
      candidate.setItem(probe, '1');
      candidate.removeItem(probe);
      return candidate;
    }
  } catch {
    // Valt hieronder terug op geheugen.
  }
  return memoryStorage();
}

export class GameState {
  money = 0;

  readonly #reputation: Record<ReputationTrack, number> = {
    police: REPUTATION_START,
    populace: REPUTATION_START,
    rivals: REPUTATION_START,
  };

  #lieutenants: Lieutenant[] = initialLieutenants();
  #territory: Territory[] = initialTerritory();
  #flags = new Map<string, boolean>();
  #relationships = new Map<string, number>();

  readonly #storage: StateStorage;
  #saveHandle: ReturnType<typeof setTimeout> | null = null;

  constructor(storage: StateStorage = defaultStorage()) {
    this.#storage = storage;
  }

  // --- geld ---------------------------------------------------------------

  /** Positief bijschrijven. Negatieve bedragen horen via `spend()`, zodat je de check niet omzeilt. */
  earn(amount: number): void {
    if (amount < 0) throw new Error('GameState.earn met een negatief bedrag; gebruik spend()');
    this.money += amount;
    this.#scheduleSave();
  }

  /** Afschrijven als het kan. Geeft terug of de betaling doorging. */
  spend(amount: number): boolean {
    if (amount < 0) throw new Error('GameState.spend met een negatief bedrag; gebruik earn()');
    if (amount > this.money) return false;
    this.money -= amount;
    this.#scheduleSave();
    return true;
  }

  // --- reputatie ----------------------------------------------------------

  reputation(track: ReputationTrack): number {
    return this.#reputation[track];
  }

  /** Verschuif een reputatiespoor. Zendt alleen een event uit als de waarde echt verandert. */
  changeReputation(track: ReputationTrack, delta: number): void {
    this.setReputation(track, this.#reputation[track] + delta);
  }

  setReputation(track: ReputationTrack, value: number): void {
    const next = clamp(value, REPUTATION_MIN, REPUTATION_MAX);
    if (next === this.#reputation[track]) return;
    this.#reputation[track] = next;
    bus.emit('reputation:changed', { track, value: next });
    this.#scheduleSave();
  }

  // --- verhaalflags en relaties -----------------------------------------

  flag(id: string): boolean {
    return this.#flags.get(id) ?? false;
  }

  setFlag(id: string, value = true): void {
    if (this.flag(id) === value) return;
    this.#flags.set(id, value);
    this.#scheduleSave();
  }

  relationship(id: RelationshipId | string): number {
    return this.#relationships.get(id) ?? 0;
  }

  changeRelationship(id: RelationshipId | string, delta: number): void {
    this.setRelationship(id, this.relationship(id) + delta);
  }

  setRelationship(id: RelationshipId | string, value: number): void {
    const next = clamp(value, -100, 100);
    if (next === this.relationship(id)) return;
    this.#relationships.set(id, next);
    this.#scheduleSave();
  }

  // --- luitenants ---------------------------------------------------------

  get lieutenants(): readonly Lieutenant[] {
    return this.#lieutenants;
  }

  lieutenant(id: LieutenantId): Lieutenant {
    const found = this.#lieutenants.find((entry) => entry.id === id);
    if (!found) throw new Error(`Onbekende luitenant "${id}"`);
    return found;
  }

  // --- territorium --------------------------------------------------------

  get territory(): readonly Territory[] {
    return this.#territory;
  }

  district(id: DistrictId): Territory {
    const found = this.#territory.find((entry) => entry.district === id);
    if (!found) throw new Error(`Onbekende wijk "${id}"`);
    return found;
  }

  /** Wijken die van Luca zijn — de basis voor de inkomsten-tick in NA-38. */
  ownedDistricts(): readonly Territory[] {
    return this.#territory.filter((entry) => entry.owner === 'vitale');
  }

  /**
   * Zet een luitenant op een wijk. Eén luitenant beheert er hooguit één, anders wordt de
   * tafellaag een lijst zonder keuzes.
   */
  assignLieutenant(district: DistrictId, id: LieutenantId | null): void {
    const target = this.district(district);
    if (id !== null) {
      this.lieutenant(id); // valideert het id
      for (const entry of this.#territory) {
        if (entry.lieutenant === id) entry.lieutenant = null;
      }
    }
    target.lieutenant = id;
    this.#scheduleSave();
  }

  // --- opslag -------------------------------------------------------------

  toJSON(): SaveData {
    return {
      version: SAVE_VERSION,
      money: this.money,
      reputation: { ...this.#reputation },
      flags: Object.fromEntries(this.#flags),
      relationships: Object.fromEntries(this.#relationships),
      lieutenants: this.#lieutenants.map((entry) => ({ ...entry })),
      territory: this.#territory.map((entry) => ({ ...entry })),
    };
  }

  /** Schrijf nu weg. `#scheduleSave()` doet dit vanzelf na een wijziging. */
  save(): void {
    if (this.#saveHandle !== null) {
      clearTimeout(this.#saveHandle);
      this.#saveHandle = null;
    }
    try {
      this.#storage.setItem(SAVE_KEY, JSON.stringify(this.toJSON()));
    } catch (error) {
      // Een volle of geweigerde opslag mag het spel niet stoppen.
      console.warn('Opslaan mislukt; het spel draait door zonder save.', error);
    }
  }

  /**
   * Laad een opgeslagen spel over deze staat heen. Geeft terug of er iets geladen is.
   *
   * Onbekende of kapotte data wordt genegeerd in plaats van gerepareerd: een half geladen
   * spel is verwarrender dan een nieuw spel.
   */
  load(): boolean {
    let raw: string | null = null;
    try {
      raw = this.#storage.getItem(SAVE_KEY);
    } catch {
      return false;
    }
    if (!raw) return false;

    let data: Partial<SaveData>;
    try {
      data = JSON.parse(raw) as Partial<SaveData>;
    } catch {
      console.warn('Save is geen geldige JSON en wordt genegeerd.');
      return false;
    }
    if (data.version !== SAVE_VERSION) {
      console.warn(`Save heeft versie ${String(data.version)}, verwacht ${SAVE_VERSION}.`);
      return false;
    }

    this.money = typeof data.money === 'number' ? data.money : 0;

    for (const track of ['police', 'populace', 'rivals'] as const) {
      const value = data.reputation?.[track];
      this.#reputation[track] = clamp(
        typeof value === 'number' ? value : REPUTATION_START,
        REPUTATION_MIN,
        REPUTATION_MAX,
      );
    }

    this.#flags.clear();
    if (data.flags && typeof data.flags === 'object') {
      for (const [id, value] of Object.entries(data.flags)) {
        if (typeof value === 'boolean') this.#flags.set(id, value);
      }
    }

    this.#relationships.clear();
    if (data.relationships && typeof data.relationships === 'object') {
      for (const [id, value] of Object.entries(data.relationships)) {
        if (typeof value === 'number') this.#relationships.set(id, clamp(value, -100, 100));
      }
    }

    if (Array.isArray(data.lieutenants) && data.lieutenants.length > 0) {
      this.#lieutenants = initialLieutenants().map((base) => {
        const saved = data.lieutenants?.find((entry) => entry.id === base.id);
        return saved
          ? {
              ...base,
              ambition: clamp(saved.ambition, 0, 1),
              loyalty: clamp(saved.loyalty, 0, 1),
            }
          : base;
      });
    }

    if (Array.isArray(data.territory) && data.territory.length > 0) {
      this.#territory = initialTerritory().map((base) => {
        const saved = data.territory?.find((entry) => entry.district === base.district);
        return saved
          ? {
              ...base,
              owner: saved.owner,
              income: saved.income,
              stability: clamp(saved.stability, 0, 1),
              lieutenant: saved.lieutenant,
            }
          : base;
      });
    }

    return true;
  }

  /** Terug naar de beginstand, en gooi de save weg. */
  reset(): void {
    this.money = 0;
    this.#reputation.police = REPUTATION_START;
    this.#reputation.populace = REPUTATION_START;
    this.#reputation.rivals = REPUTATION_START;
    this.#lieutenants = initialLieutenants();
    this.#territory = initialTerritory();
    this.#flags.clear();
    this.#relationships.clear();
    try {
      this.#storage.removeItem(SAVE_KEY);
    } catch {
      // Niets aan te doen; de staat in het geheugen klopt wel.
    }
  }

  /**
   * Sla op na een korte pauze. Een missie kan tientallen wijzigingen per seconde doen —
   * die horen één schrijfactie te worden, niet tientallen.
   */
  #scheduleSave(): void {
    if (this.#saveHandle !== null) return;
    this.#saveHandle = setTimeout(() => {
      this.#saveHandle = null;
      this.save();
    }, 400);
  }
}
